// src/screens/ReferralLanding.js
import React, { useContext } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Avatar,
  Card,
  CardContent,
} from "@mui/material";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  limit,
  getDocs,
} from "firebase/firestore";
import { db, auth } from "../@shared/firebase/config";
import { signInAnonymously } from "firebase/auth";
import SignUp from "../components/HomeScreen/SignUp";
import { motion, useInView } from "framer-motion";
import NodeDash from "../components/NodeDash";
import InfinityOrb from "../components/InfinityOrb";
import { MainContext } from "../App";
import NeoButton from "../components/NeoButton";

const ReferralLanding = () => {
  const location = useLocation();
  const { Theme, deviceType } = useContext(MainContext);
  const [loading, setLoading] = React.useState(true);
  const [referrer, setReferrer] = React.useState(null);
  const [collection, setCollection] = React.useState(null); // for collection referrals
  const [error, setError] = React.useState("");
  const [cardsRevealed, setCardsRevealed] = React.useState(false);
  const signupRef = React.useRef(null);
  const section1Ref = React.useRef(null);

  const isInView = useInView(section1Ref, {
    once: true,
    rootMargin: "-20% 0px",
  });

  React.useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError("");
      try {
        // ✅ Ensure the visitor is signed in (anonymous if needed)
        if (!auth.currentUser) {
          await signInAnonymously(auth);
        }

        // Get referrerId from query string
        const params = new URLSearchParams(location.search);
        const rawRef = (params.get("ref") || "").trim();

        // --- Branch: Collection referral (ref=col-<collectionId>) ---
        if (rawRef.startsWith("col-")) {
          const colId = rawRef.replace(/^col-/, "");
          // Load collection doc by id (you generate the referral from collection.id)
          const colSnap = await getDoc(
            doc(db, "collections_onboarding", colId)
          );
          if (!colSnap.exists()) {
            setError("That collection referral link is invalid or expired.");
            setLoading(false);
            return;
          }
          const c = colSnap.data();
          setCollection({
            id: colSnap.id,
            name: c?.name || "Collection",
            slug: c?.slug || "",
            logoUrl: c?.logoUrl || "",
            bannerUrl: c?.bannerUrl || "",
          });
          // Store for use during signup
          localStorage.removeItem("referrerId");
          localStorage.setItem("refCollectionId", colSnap.id);
        } else {
          // --- Fallback: User referral (legacy) ---
          const referrerId = rawRef;
          const refSnap = await getDoc(doc(db, "users", referrerId));
          if (!refSnap.exists()) {
            setError("That referral link is invalid or expired.");
            setLoading(false);
            return;
          }
          const data = refSnap.data();
          setReferrer({
            id: referrerId,
            username: data?.username || "Player",
            avatar: data?.avatar || null,
          });
          localStorage.setItem("referrerId", referrerId);
          localStorage.removeItem("refCollectionId");
        }
      } catch (e) {
        console.error("Referral validation error:", e);
        setError("We couldn't validate this referral. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [location.search]);

  React.useEffect(() => {
    if (isInView) {
      const t = setTimeout(() => setCardsRevealed(true), 1000);
      return () => clearTimeout(t);
    }
  }, [isInView]);

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.25, rotate: 0, x: 0 },
    visible: (index) => ({
      opacity: 1,
      scale: 1,
      rotate: [-20, 0, 20][index],
      x: deviceType === "desktop" ? [-80, 0, 80][index] : [-40, 0, 40][index],
      y: [20, -10, 20][index],
      transition: { duration: 0.8, delay: 0.5 + index * 0.2 },
    }),
  };

  const characterVariants = {
    hidden: (side) => ({
      opacity: 0,
      x: 0,
      transform: side === "left" && "scaleX(-1)",
    }),
    visible: (side) => ({
      opacity: 1,
      x: side === "left" ? -160 : deviceType === "desktop" ? 160 : 300,
      transform: side === "left" && "translate(-0%, -24%) scaleX(-1)",
      transition: { duration: 0.6, ease: "easeOut", delay: 0.5 },
    }),
  };

  const scrollToSignup = () => {
    signupRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: "100vh",
          width: "100vw",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(to bottom, #230a67, #5c0c9e)",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <motion.div
      style={{
        height: "100%",
        minHeight: "100vh",
        overflowX: "hidden",
        background: "linear-gradient(to bottom, #230a67, #5c0c9e)",
        position: "relative",
        width: "100%",
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* HERO */}
      <Grid item xs={12} sx={{ height: "100%", position: "relative" }}>
        <InfinityOrb />
        <NodeDash
          cardImages={[require("../@shared/assets/card-dash.png")]}
          itemImages={[
            require("../@shared/assets/infinityCards/ironclaw_gloves.png"),
            require("../@shared/assets/infinityCards/lifebloom_potion.png"),
            require("../@shared/assets/infinityCards/herbal_tonic.png"),
            require("../@shared/assets/infinityCards/pitfall_token.png"),
            require("../@shared/assets/infinityCards/minor_healing_potion.png"),
            require("../@shared/assets/infinityCards/boneblade_fragment.png"),
            require("../@shared/assets/infinityCards/pulse_of_life.png"),
            require("../@shared/assets/infinityCards/venom_tipped_blade.png"),
            require("../@shared/assets/infinityCards/crimson_edge_shard.png"),
            require("../@shared/assets/infinityCards/crystal_armor_shard.png"),
            require("../@shared/assets/infinityCards/ghoststeel_spike.png"),
          ]}
          spawnRate={600}
        />

        {/* Logo */}
        <motion.img
          initial={{
            opacity: 0,
            top: deviceType === "desktop" ? "40%" : "26%",
          }}
          animate={{
            opacity: 1,
            top: deviceType === "desktop" ? "30%" : "30%",
          }}
          transition={{ duration: 1.2 }}
          src={require("../@shared/assets/logo-icon-text-reg.png")}
          style={{
            height: "auto",
            width: deviceType === "desktop" ? "26%" : "58%",
            position: "absolute",
            left: "50%",
            top: "30%",
            transform: "translate(-50%, -50%)",
            zIndex: 2,
          }}
        />

        {/* Invite Banner */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          style={{
            position: "absolute",
            top: deviceType === "desktop" ? "46%" : "46%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 3,
            width: deviceType === "desktop" ? "42%" : "88%",
          }}
        >
          {error ? (
            <Alert severity="warning">{error}</Alert>
          ) : (
            <Card
              elevation={6}
              sx={{
                bgcolor: "#120334",
                border: `1px solid ${Theme.palette.primary.main}50`,
                color: "#fff",
                borderRadius: 3,
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  flexFlow: { xs: "column", md: "row" },
                  alignItems: "center",
                  gap: 2,
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {collection ? (
                    <>
                      <Avatar
                        src={
                          collection.logoUrl ||
                          "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/avatars%2FinfintyOrb%2Fio-green.png?alt=media&token=feff4088-f1da-43f7-9c7c-c231fe45b240"
                        }
                        alt={collection.name}
                        sx={{
                          width: 56,
                          height: 56,
                          border: "2px solid #311a79",
                        }}
                      />
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: "#9fa8da",
                            textTransform: "uppercase",
                            letterSpacing: 1,
                          }}
                        >
                          You’re supporting
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 800, lineHeight: 1.1 }}
                        >
                          {collection.name}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Avatar
                        src={
                          referrer?.avatar?.image ||
                          "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/avatars%2FinfintyOrb%2Fio-green.png?alt=media&token=feff4088-f1da-43f7-9c7c-c231fe45b240"
                        }
                        alt={referrer?.username}
                        sx={{
                          width: 48,
                          height: 48,
                          border: "2px solid #311a79",
                        }}
                      />
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: "#9fa8da",
                            textTransform: "uppercase",
                            letterSpacing: 1,
                          }}
                        >
                          You were invited by
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 800, lineHeight: 1.1 }}
                        >
                          @{referrer?.username}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
                <NeoButton
                  color={Theme.palette.primary.main}
                  text={collection ? "Join & Support" : "Join the Battle"}
                  width={deviceType === "desktop" ? 50 : 100}
                  onClick={scrollToSignup}
                />
              </CardContent>
            </Card>
          )}
        </motion.div>

        {/* CTA under banner */}
        <motion.div
          initial={{ opacity: 0, top: "71%" }}
          animate={{ opacity: 1, top: "65%" }}
          transition={{ duration: 1.2, delay: 0.9 }}
          style={{
            position: "absolute",
            top: "65%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            zIndex: 3,
            width: deviceType === "desktop" ? "44%" : "90%",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: "white",
              fontWeight: 600,
              textShadow: "2px 3px 6px #000",
              textAlign: "center",
            }}
          >
            Sign up now to auto‑friend your referrer & start playing.
          </Typography>
        </motion.div>
      </Grid>

      {/* INFO SECTION 1 (animated fanned cards + characters) */}
      <Grid
        ref={section1Ref}
        container
        item
        xs={12}
        sx={{
          height: deviceType === "desktop" ? "90vh" : "60vh",
          alignItems: deviceType === "desktop" ? "center" : "flex-end",
          justifyContent: "center",
          color: "#fff",
          position: "relative",
          mt: 6,
        }}
      >
        <motion.img
          src={require("../@shared/assets/red-wizard-start.png")}
          variants={characterVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-20% 0px" }}
          animate={cardsRevealed ? "visible" : "hidden"}
          custom="left"
          style={{
            height: deviceType === "desktop" ? "36%" : "50%",
            width: "auto",
            position: "absolute",
            left: deviceType === "desktop" ? "22%" : "-20%",
            transform: "translate(-50%, -50%)",
            zIndex: 1,
          }}
        />
        {[0, 1, 2].map((index) => (
          <motion.img
            key={index}
            src={require("../@shared/assets/back-of-card.png")}
            custom={index}
            variants={cardVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-20% 0px" }}
            animate={cardsRevealed ? "visible" : "hidden"}
            style={{
              height: deviceType === "desktop" ? "38%" : "28%",
              width: "auto",
              position: "absolute",
              left: "42%",
              top: "24%",
              transform: "translate(-50%, -50%)",
              zIndex: 100,
              boxShadow: "0 0 10px rgba(0, 0, 0, 0.5)",
            }}
          />
        ))}
        <motion.img
          src={require("../@shared/assets/nox-start.png")}
          variants={characterVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-20% 0px" }}
          animate={cardsRevealed ? "visible" : "hidden"}
          custom="right"
          style={{
            height: deviceType === "desktop" ? "40%" : "58%",
            width: "auto",
            position: "absolute",
            right: "28%",
            top: "20%",
            transform: "translate(50%, -50%)",
            zIndex: 1,
          }}
        />
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-20% 0px" }}
          animate={cardsRevealed ? "visible" : "hidden"}
          variants={{
            hidden: {},
            visible: {
              transition: { staggerChildren: 0.25, delayChildren: 0.6 },
            },
          }}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -30%)",
            textAlign: "center",
            zIndex: 200,
            width: "100%",
          }}
        >
          {["CARD SLAPS.", "SPELL BLASTS.", "TOTAL CHAOS."].map((line, i) => (
            <motion.div
              key={i}
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.6 },
                },
              }}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-20% 0px" }}
              style={{
                fontSize: deviceType === "desktop" ? "6rem" : "2.3rem",
                fontWeight: 800,
                color: Theme.palette.text.main,
                textShadow: `2px 3px 8px  #000`,
                lineHeight: 0.9,
                width: "100%",
                textAlign: "center",
              }}
            >
              {line}
            </motion.div>
          ))}
        </motion.div>
      </Grid>

      {/* SIGN UP PANEL */}
      <Grid
        ref={signupRef}
        container
        item
        xs={12}
        sx={{
          justifyContent: "center",
          pb: 10,
          pt: 4,
          background: "linear-gradient(to top, #200662, #2f0b7300)",
        }}
      >
        <Grid
          item
          xs={11}
          md={8}
          lg={6}
          component={motion.div}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card
            elevation={10}
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              border: `1px solid ${Theme.palette.primary.main}50`,
              bgcolor: "#120334",
            }}
          >
            <Box
              sx={{
                p: 3,
                display: "flex",
                alignItems: "center",
                gap: 2,
                borderBottom: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <Avatar
                src={
                  collection?.logoUrl ||
                  referrer?.avatar?.image ||
                  "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/avatars%2FinfintyOrb%2Fio-green.png?alt=media&token=feff4088-f1da-43f7-9c7c-c231fe45b240"
                }
                alt={collection ? collection.name : referrer?.username}
                sx={{ width: 48, height: 48, border: "2px solid #311a79" }}
              />
              <Box>
                <Typography
                  sx={{
                    color: "#9fa8da",
                    fontSize: 12,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                  }}
                >
                  {collection
                    ? "Collection Referral Active"
                    : "Referral Active"}
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{ color: "#fff", fontWeight: 700 }}
                >
                  {error
                    ? "No referrer — you can still sign up!"
                    : collection
                    ? `You’ll support ${collection.name} at signup`
                    : `You’ll auto-friend @${referrer?.username}`}
                </Typography>
              </Box>
            </Box>
            <CardContent
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                p: deviceType === "desktop" ? 4 : 2,
                bgcolor: "#fff",
              }}
            >
              <SignUp isReferral={true} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* FOOTER */}
      <motion.footer
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{
          width: "100%",
          backgroundColor: "#0e0326",
          padding: "3rem 1.5rem",
          color: "#fff",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          zIndex: 5,
        }}
      >
        <img
          src={require("../@shared/assets/logo-icon-text-reg.png")}
          alt="Logo"
          style={{ height: 75, marginBottom: "1rem" }}
        />
        <Typography variant="body2" sx={{ fontSize: 12, color: "#888", mt: 2 }}>
          © {new Date().getFullYear()} InfiNFT Royale. All rights reserved.
        </Typography>
      </motion.footer>
    </motion.div>
  );
};

export default ReferralLanding;
